import React, { useState } from "react";

export interface PCBConfig {
  layers: number;
  board_width_mm: number;
  board_height_mm: number;
  min_track_mm: number;
  min_clearance_mm: number;
  edge_clearance_mm: number;
  via_drill_mm: number;
  via_diameter_mm: number;
  copper_weight_oz: number;
  board_thickness_mm: number;
}

const DEFAULT_CONFIG: PCBConfig = {
  layers: 4,
  board_width_mm: 80,
  board_height_mm: 60,
  min_track_mm: 0.2,
  min_clearance_mm: 0.2,
  edge_clearance_mm: 0.5,
  via_drill_mm: 0.3,
  via_diameter_mm: 0.6,
  copper_weight_oz: 1,
  board_thickness_mm: 1.6,
};

const PRESETS: Record<string, { label: string; description: string; config: PCBConfig }> = {
  industrial_4layer: {
    label: "Industrial 4-Layer (Recommended)",
    description: "Signal/GND/Power/Signal stackup. Best for 24V industrial CAN nodes.",
    config: { layers: 4, board_width_mm: 80, board_height_mm: 60, min_track_mm: 0.2, min_clearance_mm: 0.2, edge_clearance_mm: 0.5, via_drill_mm: 0.3, via_diameter_mm: 0.6, copper_weight_oz: 1, board_thickness_mm: 1.6 },
  },
  prototype_2layer: {
    label: "Prototype 2-Layer",
    description: "Low-cost first board. Moderate EMI risk, slower CAN.",
    config: { layers: 2, board_width_mm: 80, board_height_mm: 60, min_track_mm: 0.25, min_clearance_mm: 0.25, edge_clearance_mm: 0.5, via_drill_mm: 0.4, via_diameter_mm: 0.8, copper_weight_oz: 1, board_thickness_mm: 1.6 },
  },
  emi_focused_4layer: {
    label: "EMI-Focused 4-Layer",
    description: "Tighter constraints for better GND/power integrity.",
    config: { layers: 4, board_width_mm: 80, board_height_mm: 60, min_track_mm: 0.15, min_clearance_mm: 0.15, edge_clearance_mm: 0.3, via_drill_mm: 0.25, via_diameter_mm: 0.5, copper_weight_oz: 1, board_thickness_mm: 1.6 },
  },
  compact_6layer: {
    label: "Compact 6-Layer",
    description: "Only if board size is constrained. Higher cost.",
    config: { layers: 6, board_width_mm: 60, board_height_mm: 45, min_track_mm: 0.15, min_clearance_mm: 0.15, edge_clearance_mm: 0.3, via_drill_mm: 0.2, via_diameter_mm: 0.45, copper_weight_oz: 1, board_thickness_mm: 1.6 },
  },
};

interface PCBConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (config: PCBConfig) => void;
  initialConfig?: Partial<PCBConfig>;
}

const PCBConfigModal: React.FC<PCBConfigModalProps> = ({ isOpen, onClose, onSave, initialConfig }) => {
  const [config, setConfig] = useState<PCBConfig>({ ...DEFAULT_CONFIG, ...initialConfig });

  if (!isOpen) return null;

  const handleSave = () => {
    onSave(config);
    onClose();
  };

  const update = (key: keyof PCBConfig, value: number) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className="pcb-modal-overlay" onClick={onClose}>
      <div className="pcb-modal" onClick={(e) => e.stopPropagation()}>
        <h3>⚙️ PCB Configuration</h3>

        {/* Presets */}
        <div className="pcb-presets">
          <label className="pcb-preset-label">Quick Preset:</label>
          <div className="pcb-preset-buttons">
            {Object.entries(PRESETS).map(([key, preset]) => (
              <button
                key={key}
                className={`pcb-preset-btn ${config.layers === preset.config.layers && config.min_track_mm === preset.config.min_track_mm ? 'pcb-preset-active' : ''}`}
                onClick={() => setConfig({ ...preset.config })}
                title={preset.description}
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>

        <div className="pcb-modal-grid">
          <label>Layers</label>
          <select value={config.layers} onChange={(e) => update("layers", Number(e.target.value))}>
            <option value={2}>2 layers</option>
            <option value={4}>4 layers</option>
            <option value={6}>6 layers</option>
          </select>

          <label>Board Width (mm)</label>
          <input type="number" value={config.board_width_mm} min={20} max={300} step={5}
            onChange={(e) => update("board_width_mm", Number(e.target.value))} />

          <label>Board Height (mm)</label>
          <input type="number" value={config.board_height_mm} min={20} max={300} step={5}
            onChange={(e) => update("board_height_mm", Number(e.target.value))} />

          <label>Min Track (mm)</label>
          <select value={config.min_track_mm} onChange={(e) => update("min_track_mm", Number(e.target.value))}>
            <option value={0.1}>0.10 mm</option>
            <option value={0.15}>0.15 mm</option>
            <option value={0.2}>0.20 mm</option>
            <option value={0.25}>0.25 mm</option>
            <option value={0.3}>0.30 mm</option>
          </select>

          <label>Min Clearance (mm)</label>
          <select value={config.min_clearance_mm} onChange={(e) => update("min_clearance_mm", Number(e.target.value))}>
            <option value={0.15}>0.15 mm</option>
            <option value={0.2}>0.20 mm</option>
            <option value={0.25}>0.25 mm</option>
          </select>

          <label>Edge Clearance (mm)</label>
          <select value={config.edge_clearance_mm} onChange={(e) => update("edge_clearance_mm", Number(e.target.value))}>
            <option value={0.2}>0.20 mm</option>
            <option value={0.3}>0.30 mm</option>
            <option value={0.5}>0.50 mm</option>
            <option value={1.0}>1.00 mm</option>
          </select>

          <label>Via Drill (mm)</label>
          <select value={config.via_drill_mm} onChange={(e) => update("via_drill_mm", Number(e.target.value))}>
            <option value={0.2}>0.20 mm</option>
            <option value={0.3}>0.30 mm</option>
            <option value={0.4}>0.40 mm</option>
          </select>

          <label>Via Diameter (mm)</label>
          <select value={config.via_diameter_mm} onChange={(e) => update("via_diameter_mm", Number(e.target.value))}>
            <option value={0.45}>0.45 mm</option>
            <option value={0.6}>0.60 mm</option>
            <option value={0.8}>0.80 mm</option>
          </select>

          <label>Copper Weight</label>
          <select value={config.copper_weight_oz} onChange={(e) => update("copper_weight_oz", Number(e.target.value))}>
            <option value={1}>1 oz</option>
            <option value={2}>2 oz</option>
          </select>

          <label>Board Thickness (mm)</label>
          <select value={config.board_thickness_mm} onChange={(e) => update("board_thickness_mm", Number(e.target.value))}>
            <option value={0.8}>0.8 mm</option>
            <option value={1.0}>1.0 mm</option>
            <option value={1.6}>1.6 mm</option>
            <option value={2.0}>2.0 mm</option>
          </select>
        </div>

        <div className="pcb-modal-actions">
          <button className="pcb-modal-cancel" onClick={onClose}>Cancel</button>
          <button className="pcb-modal-save" onClick={handleSave}>💾 Save & Continue</button>
        </div>
      </div>
    </div>
  );
};

export default PCBConfigModal;
export { DEFAULT_CONFIG };
